import { useState } from 'react';
import { Menu, X, ChevronDown, ChevronUp } from 'lucide-react';
import { Link, NavLink } from 'react-router-dom';
import logo from '../../public/logo.png';
import { ServicesMenu } from './ServicesMenu';
import { servicesMenu } from "./ServicesData";
import type { ServiceCategory, ServiceItem } from "./ServicesData";

// Utility function for active link styling
const activeClass = ({ isActive }: { isActive: boolean }) =>
  isActive
    ? 'text-[#05326D] hover:text-blue-900 px-1 lg:px-3 py-2 text-xs lg:text-sm font-medium transition-colors active-link'
    : 'text-gray-700 hover:text-blue-900 px-1 lg:px-3 py-2 text-xs lg:text-sm font-medium transition-colors';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };
  
  const toggleSection = (sectionKey: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionKey]: !prev[sectionKey]
    }));
  };

  return (
    <header className="bg-white shadow-lg relative z-50">
      <nav className="max-w-[var(--max-width)] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="flex items-center space-x-2">
                <Link className="flex flex-col" to="/">
                  <img
                    className="xl:w-[320px] lg:w-[240px] md:w-[320px] sm:w-[260px] w-[220px] bg-white mt-[30px]"
                    style={{ borderRadius: '10px' }}
                    src={logo}
                    alt="Logo"
                  />
                </Link>
              </div>
            </div>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:block">
            <div className="ml-10 flex items-baseline lg:space-x-8">
              <NavLink to="/" className={activeClass}>
                Home
              </NavLink>
              <ServicesMenu />
              <NavLink to="/about-us" className={activeClass}>
                About Us
              </NavLink>
              <NavLink to="/publications" className={activeClass}>
                Publications
              </NavLink>
              <NavLink to="/faqs" className={activeClass}>
                FAQs
              </NavLink>
              <NavLink to="/contact-us" className={activeClass}>
                Contact Us
              </NavLink>
              <NavLink to="/our-expertise" className={activeClass}>
                Our Expertise
              </NavLink>
            </div>
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden">
            <button
              onClick={toggleMenu}
              className="inline-flex items-center justify-center p-2 rounded-md text-gray-700 hover:text-blue-900 hover:bg-gray-300 transition-colors"
            >
              {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden bg-white mt-2">
            <div className="max-w-sm mx-auto">
              {/* Additional Navigation Items */}
              <div className="px-4 py-3 space-y-3">
                <NavLink to="/" className="block px-3 py-1 text-gray-700 hover:text-blue-900 hover:bg-gray-50 transition-colors duration-200" onClick={() => setIsMenuOpen(false)}>
                  Home
                </NavLink>                
                  <button
                  onClick={() => toggleSection('services')}
                  className="w-full flex items-center justify-between px-3 text-gray-700 hover:text-blue-900 hover:bg-gray-50 transition-colors duration-200"
                >
                  <span className='text-gray-700 hover:text-blue-900 hover:bg-gray-50 transition-colors duration-200'>Services</span>
                  {expandedSections['services'] ? (
                    <ChevronUp className="w-5 h-5 text-gray-600" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-600" />
                  )}
                </button>
                {expandedSections['services'] && (
                  <div className="pl-6 pb-3">
                    {/* Direct Service Categories */}
                    <div className="space-y-2">
                      {servicesMenu.map((category: ServiceCategory, i: number) => (
                        <div key={i}>
                          <button
                            onClick={() => toggleSection(`category-${i}`)}
                            className="w-full flex items-center justify-between py-2 text-left text-gray-700 font-medium hover:text-blue-900 transition-colors duration-200"
                          >
                            <span className="text-sm">{category.title}</span>
                            {expandedSections[`category-${i}`] ? (
                              <ChevronUp className="w-4 h-4 text-gray-600" />
                            ) : (
                              <ChevronDown className="w-4 h-4 text-gray-600" />
                            )}
                          </button>
                          {expandedSections[`category-${i}`] && (
                            <div className="pl-1 space-y-1 mt-1 rounded-md">
                              {category.items.map((item: ServiceItem, j: number) => (
                                <Link
                                  key={j}
                                  to={item.path}
                                  className="block py-1 px-2 text-xs text-gray-600 hover:text-blue-900 hover:bg-gray-50 rounded transition-colors duration-200"
                                  onClick={() => setIsMenuOpen(false)}
                                >
                                  {item.name}
                                </Link>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <NavLink to="/about-us" className="block px-3 py-1 text-gray-700 hover:text-blue-900 hover:bg-gray-50 transition-colors duration-200" onClick={() => setIsMenuOpen(false)}>
                  About Us
                </NavLink>
                <NavLink to="/publications" className="block px-3 py-1 text-gray-700 hover:text-blue-900 hover:bg-gray-50 transition-colors duration-200" onClick={() => setIsMenuOpen(false)}>
                  Publications
                </NavLink>
                <NavLink to="/faqs" className="block px-3 py-1 text-gray-700 hover:text-blue-900 hover:bg-gray-50 transition-colors duration-200" onClick={() => setIsMenuOpen(false)}>
                  FAQs
                </NavLink>
                <NavLink to="/contact-us" className="block px-3 py-1 text-gray-700 hover:text-blue-900 hover:bg-gray-50 transition-colors duration-200" onClick={() => setIsMenuOpen(false)}>
                  Contact Us
                </NavLink>
                <NavLink to="/our-expertise" className="block px-3 py-1 text-gray-700 hover:text-blue-900 hover:bg-gray-50 transition-colors duration-200" onClick={() => setIsMenuOpen(false)}>
                  Our Expertise
                </NavLink>
              </div>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;